import { ListRepository } from '@/repository/list/ListRepository';
import * as APITypes from '@/API';
import { BoughtList, ExclusionRule } from '@/API';
import { GraphQLResult } from 'aws-amplify/api';
import log from 'loglevel';
import * as queries from '@/graphql/queries';
import { GraphQLOptionsV6 } from '@aws-amplify/api-graphql';
import { FilterDefinition } from '@lambda-layer/models/FilterDefinition/FilterDefinition.ts';
import { FilterSelections } from '@lambda-layer/types/filter.ts';
import { buildCountQuery } from '@lambda-layer/utils/queryUtils';

type GeneratedQuery<InputType, OutputType> = string & {
  __generatedQueryInput: InputType;
  __generatedQueryOutput: OutputType;
};

export class BoughtListRepository extends ListRepository<BoughtList> {
  /**
   * Updates the opt-out status of a bought list item using secure backend mutation.
   *
   * @param id - The ID of the bought list item.
   * @param value - The new opt-out value.
   * @returns The updated bought list item.
   * @throws Error Will throw an error if the mutation fails.
   */
  async optOut(id: string, value: boolean) {
    try {
      const result = (await this.client.graphql({
        query: `mutation UpdateBoughtListOptOut($id: ID!, $optOut: Boolean!) {
          updateBoughtListOptOut(id: $id, optOut: $optOut) {
            id
            optOut
            optOutDateTime
            optOutUpdatedBy
            __typename
          }
        }`,
        variables: {
          id,
          optOut: value,
        },
      })) as GraphQLResult<{ updateBoughtListOptOut: BoughtList }>;
      return result.data.updateBoughtListOptOut;
    } catch (error) {
      log.error(error);
      throw new Error('Failed to update opt out');
    }
  }

  public async search(queryVariables: GraphQLOptionsV6['variables']) {
    return (
      await this.client.graphql({
        query: queries.searchBoughtLists,
        variables: queryVariables,
      })
    ).data.searchBoughtLists;
  }

  public async searchProspects(queryVariables: GraphQLOptionsV6['variables']) {
    const searchProspects = /* GraphQL */ `query SearchBoughtLists(
        $filter: SearchableBoughtListFilterInput
        $sort: [SearchableBoughtListSortInput]
        $limit: Int
        $nextToken: String
        $from: Int
        $aggregates: [SearchableBoughtListAggregationInput]
    ) {
        searchBoughtLists(
            filter: $filter
            sort: $sort
            limit: $limit
            nextToken: $nextToken
            from: $from
            aggregates: $aggregates
        ) {
            items {
                id
                firstName
                lastName
                address
                __typename
            }
            nextToken
            total
            __typename
        }
    }
    ` as GeneratedQuery<
      APITypes.SearchBoughtListsQueryVariables,
      APITypes.SearchBoughtListsQuery
    >;
    return (
      await this.client.graphql({
        query: searchProspects,
        variables: queryVariables,
      })
    ).data.searchBoughtLists;
  }

  async count(
    filterDefinitions: FilterDefinition[],
    filterSelections: FilterSelections,
    exclusions?: ExclusionRule[]
  ): Promise<number> {
    const { data } = (await this.client.graphql({
      query: buildCountQuery(
        'searchBoughtLists',
        filterDefinitions,
        filterSelections,
        exclusions || []
      ),
    })) as GraphQLResult<APITypes.SearchBoughtListsQuery>;
    return data.searchBoughtLists?.total || 0;
  }

  protected async getSearchableAggregateBucketResultItems(
    dbColumn: string,
    filterDefinitions: FilterDefinition[],
    filterSelections: FilterSelections,
    exclusions?: ExclusionRule[]
  ): Promise<{
    buckets: (APITypes.SearchableAggregateBucketResultItem | null)[];
    total: number;
  }> {
    const { data } = (await this.client.graphql({
      query: this.createAggregateQuery(
        dbColumn,
        'searchBoughtLists',
        filterDefinitions,
        filterSelections,
        exclusions || []
      ),
    })) as GraphQLResult<APITypes.SearchBoughtListsQuery>;

    const total = data?.searchBoughtLists?.total ?? 0;

    const buckets =
      (
        data?.searchBoughtLists?.aggregateItems?.[0]
          ?.result as APITypes.SearchableAggregateBucketResult
      )?.buckets ?? [];

    return { buckets, total };
  }
}
